/**
 * Authored content: shapes the DESIGNER wrote, not the agent or the student.
 *
 * A practice bite's problem statement (title, question, diagram) is composed in
 * studio, frozen on finalize, and drawn onto the notepad before the lesson
 * starts. It is the question — the student must not be able to move it, erase
 * it, or drag it off the page while working. The agent still can: it needs to
 * cross out, annotate and re-lay-out the problem while teaching, and every
 * agent mutation runs inside `editor.run(..., { ignoreShapeLock: true })` with
 * `isActingOnEditor` set, which clears both guards.
 *
 * These shapes are seeded at connection open with a NEUTRAL intent, so the
 * signal is `authoredLayout: true`, stamped by the gateway's premade expansion
 * (`src/agent/premade-expansion.ts`). It is not in the et-ui action schema, so
 * it is read off the wire object, which CANVAS_SEED leaves un-zod-parsed.
 */
export function isAuthoredContentAction(action: object): boolean {
  // Strictly `true`: unvalidated off the wire, and a truthy check would let
  // malformed input lock a student's shape.
  return (action as { authoredLayout?: unknown }).authoredLayout === true;
}

/** Strictly `true`: unvalidated off the wire, and it unprotects the shape it creates. */
export function isStudentAuthoredAction(action: object): boolean {
  return (action as { studentAuthored?: unknown }).studentAuthored === true;
}

/**
 * `meta.source` value marking a shape as authored content.
 *
 * DO NOT CHANGE THIS STRING. It is persisted: sessionStorage canvas snapshots
 * written by earlier builds carry `meta.source === "premade"`, and the
 * delete-guard matches on it. Renaming the value would silently unprotect
 * every restored snapshot — the shapes would come back looking ordinary.
 */
export const AUTHORED_SOURCE = "premade";

/** True for a shape carrying the authored-content tag. */
export function isAuthoredShape(shape: {
  meta?: Record<string, unknown>;
}): boolean {
  return shape.meta?.source === AUTHORED_SOURCE;
}
